import { useEffect, useState } from "react";
import {
  Calendar,
  Download,
  FileSpreadsheet,
  ListChecks,
  Loader2,
  X,
} from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Card } from "../../components/Card/Card";
import { DataTable, type Column } from "../../components/DataTable/DataTable";
import { FilterBar } from "../../components/FilterBar/FilterBar";
import { StatusPill } from "../../components/StatusPill/StatusPill";
import { TopNav } from "../../components/TopNav/TopNav";
import { type CaseRow, type ExcelExport, api } from "../../lib/api";
import { useSelection } from "../../lib/SelectionContext";
import { formatDateTime } from "../../lib/format";

import styles from "./EnviarCasosPage.module.css";

const PAGE_SIZE = 100;
const SENDABLE_STATES = "Por Enviar,Por Reenviar";
const MAX_EXPORT = 290;

export default function EnviarCasosPage() {
  const { selected, toggle, addMany, removeMany, clear } = useSelection();
  const [cases, setCases] = useState<CaseRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [exports, setExports] = useState<ExcelExport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tipoOptions, setTipoOptions] = useState<string[]>([]);

  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedDate, setAppliedDate] = useState("");
  const [tipoDoctos, setTipoDoctos] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  function buildParams(p: number, searchVal: string, dateVal: string, tipos: string[], pageSize = PAGE_SIZE) {
    const params = new URLSearchParams();
    params.set("page", String(p));
    params.set("page_size", String(pageSize));
    params.set("ordering", "-created_at");
    params.set("Estado__in", SENDABLE_STATES);
    if (searchVal.trim()) params.set("search", searchVal.trim());
    if (dateVal) params.set("created_at__date", dateVal);
    if (tipos.length > 0) params.set("TipoDocto__in", tipos.join(","));
    return params;
  }

  async function fetchCases(p: number, searchVal: string, dateVal: string, tipos: string[]) {
    const c = await api.listCases(buildParams(p, searchVal, dateVal, tipos));
    setCases(c.results);
    setTotalCount(c.count);
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [, x, tipos] = await Promise.all([
        fetchCases(page, appliedSearch, appliedDate, tipoDoctos),
        api.listExports(),
        api.caseTipoOptions(),
      ]);
      setExports(x.results);
      setTipoOptions(tipos);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageRows = cases;

  const allOnPageSelected =
    pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const atLimit = selected.size >= MAX_EXPORT;

  function togglePage() {
    if (allOnPageSelected) {
      removeMany(pageRows.map((r) => r.id));
      return;
    }
    const remaining = MAX_EXPORT - selected.size;
    const toAdd = pageRows.filter((r) => !selected.has(r.id)).slice(0, remaining);
    addMany(toAdd.map((r) => r.id));
    if (toAdd.length < pageRows.filter((r) => !selected.has(r.id)).length) {
      setError(`Limite de ${MAX_EXPORT} casos por exportação atingido.`);
    }
  }

  const allResultsSelected = totalCount > 0 && selected.size >= Math.min(totalCount, MAX_EXPORT);

  async function toggleAllFiltered() {
    if (allResultsSelected) {
      clear();
      return;
    }
    setSelectingAll(true);
    try {
      const c = await api.listCases(
        buildParams(1, appliedSearch, appliedDate, tipoDoctos, MAX_EXPORT)
      );
      addMany(c.results.map((r) => r.id));
      if (totalCount > MAX_EXPORT) {
        setError(`Limite de ${MAX_EXPORT} casos por exportação. Foram selecionados os primeiros ${MAX_EXPORT}.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível selecionar todos.");
    } finally {
      setSelectingAll(false);
    }
  }

  function toggleTipo(value: string) {
    const newTipos = tipoDoctos.includes(value)
      ? tipoDoctos.filter((x) => x !== value)
      : [...tipoDoctos, value];
    setTipoDoctos(newTipos);
    setPage(1);
    setLoading(true);
    fetchCases(1, appliedSearch, appliedDate, newTipos)
      .catch((err) => setError(err instanceof Error ? err.message : "Erro"))
      .finally(() => setLoading(false));
  }

  function clearTipos() {
    setTipoDoctos([]);
    setPage(1);
    setLoading(true);
    fetchCases(1, appliedSearch, appliedDate, [])
      .catch((err) => setError(err instanceof Error ? err.message : "Erro"))
      .finally(() => setLoading(false));
  }

  async function handleApply() {
    setAppliedSearch(search);
    setAppliedDate(date);
    setPage(1);
    setLoading(true);
    try {
      await fetchCases(1, search, date, tipoDoctos);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }

  async function handleClear() {
    setSearch("");
    setDate("");
    setAppliedSearch("");
    setAppliedDate("");
    setTipoDoctos([]);
    setPage(1);
    setLoading(true);
    try {
      await fetchCases(1, "", "", []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }

  async function goToPage(p: number) {
    setPage(p);
    setLoading(true);
    try {
      await fetchCases(p, appliedSearch, appliedDate, tipoDoctos);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível carregar os dados.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGenerate() {
    if (selected.size === 0) {
      setError("Selecione pelo menos um caso para gerar o ficheiro.");
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const ids = Array.from(selected);
      const result = await api.generateExport(ids);
      clear();
      const skipped = ids.length - result.case_count;
      if (skipped > 0) {
        setError(
          `Ficheiro gerado com ${result.case_count} caso(s). ${skipped} caso(s) foram excluídos por já terem sido exportados por outro utilizador.`
        );
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao gerar o ficheiro Excel.");
    } finally {
      setGenerating(false);
    }
  }

  const columns: Column<CaseRow>[] = [
    {
      key: "select",
      header: (
        <label className={styles.checkbox} aria-label="Selecionar página">
          <input
            type="checkbox"
            checked={allOnPageSelected}
            onChange={togglePage}
          />
        </label>
      ),
      render: (row) => (
        <label className={styles.checkbox} aria-label={`Selecionar caso ${row.MovNum}`}>
          <input
            type="checkbox"
            checked={selected.has(row.id)}
            onChange={() => {
              if (!selected.has(row.id) && atLimit) {
                setError(`Limite de ${MAX_EXPORT} casos por exportação atingido.`);
                return;
              }
              toggle(row.id);
            }}
          />
        </label>
      ),
      width: "44px",
    },
    {
      key: "cod",
      header: "CodProjeto",
      render: (row) => row.CodProjeto,
      sortable: true,
      sortValue: (row) => row.CodProjeto,
    },
    {
      key: "mov",
      header: "MovNum",
      render: (row) => `${String(row.MovNum).padStart(7, "0")}`,
      sortable: true,
      sortValue: (row) => row.MovNum,
    },
    {
      key: "nif",
      header: "NIFDestinatário",
      render: (row) => row.NIFDestinatario,
      sortable: true,
      sortValue: (row) => row.NIFDestinatario,
    },
    {
      key: "tipo",
      header: "Tipo",
      render: (row) => row.TipoDocto,
      sortable: true,
      sortValue: (row) => row.TipoDocto,
    },
    {
      key: "estado",
      header: "Estado",
      render: (row) => {
        switch (row.Estado) {
          case "Enviado":
            return <StatusPill tone="sent">Enviado</StatusPill>;
          case "Por Reenviar":
            return <StatusPill tone="resend">Por Reenviar</StatusPill>;
          default:
            return <StatusPill tone="to-send">Por enviar</StatusPill>;
        }
      },
      width: "130px",
      sortable: true,
      sortValue: (row) => row.Estado,
    },
  ];

  const lastExport = exports[0] ?? null;

  return (
    <div className={styles.page}>
      <TopNav />

      <main className={styles.main}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.filtersWrap}>
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            dateValue={date}
            onDateChange={setDate}
            onApply={() => void handleApply()}
            onClear={() => void handleClear()}
          />
          <div className={styles.tipoBar}>
            <span className={styles.tipoLabel}>Tipo:</span>
            {tipoOptions.map((t) => (
              <button
                key={t}
                type="button"
                className={`${styles.tipoChip} ${tipoDoctos.includes(t) ? styles.tipoChipActive : ""}`}
                onClick={() => toggleTipo(t)}
              >
                {t}
              </button>
            ))}
            {tipoDoctos.length > 0 && (
              <button type="button" className={styles.tipoClear} onClick={clearTipos}>
                <X size={11} />
                limpar
              </button>
            )}
          </div>
        </div>

        <div className={styles.layout}>
          <Card
            title="Casos para envio"
            className={styles.leftCard}
            toolbar={
              <div className={styles.toolbarGroup}>
                <button type="button" className={styles.toolbarToggle} onClick={togglePage}>
                  <span className={styles.checkbox}>
                    <input type="checkbox" checked={allOnPageSelected} readOnly />
                  </span>
                  Selecionar página
                </button>
                <button
                  type="button"
                  className={styles.toolbarToggle}
                  onClick={() => void toggleAllFiltered()}
                  disabled={selectingAll || totalCount === 0}
                >
                  {selectingAll
                    ? <Loader2 size={12} className={styles.spin} />
                    : null}
                  {allResultsSelected ? "Remover todos" : `Selecionar todos (${totalCount})`}
                </button>
              </div>
            }
          >
            <DataTable
              columns={columns}
              rows={pageRows}
              rowKey={(r) => r.id}
              empty={loading ? "A carregar…" : "Sem casos para enviar."}
            />
            <Pagination page={page} totalPages={totalPages} onChange={(p) => void goToPage(p)} />
          </Card>

          <div className={styles.rightCol}>
            <Card title="Resumo da geração" className={styles.summaryCard}>
              <div className={styles.summaryGrid}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryDot}>
                    <ListChecks size={16} />
                  </span>
                  <span className={styles.summaryItemLabel}>Casos selecionados</span>
                  <span className={styles.summaryItemValue}>
                    {selected.size}
                    <span className={styles.summaryItemMax}> / {MAX_EXPORT}</span>
                  </span>
                </div>
              </div>
            </Card>

            <Card title="Gerar ficheiro Excel" className={styles.generateCard}>
              <div className={styles.generateBox}>
                <div className={styles.generateMain}>
                  <Button
                    variant="success"
                    onClick={handleGenerate}
                    disabled={generating || selected.size === 0}
                    className={styles.generateButton}
                  >
                    <FileSpreadsheet size={16} />
                    {generating ? "A GERAR…" : "GERAR FICHEIRO EXCEL"}
                  </Button>
                </div>
                <p className={styles.note}>
                  O ficheiro será gerado com os casos selecionados acima. Casos em estado
                  «Erro» são automaticamente excluídos da exportação.
                </p>
              </div>
            </Card>

            <Card title="Último ficheiro gerado" className={styles.lastCard}>
              <div className={styles.lastBody}>
                {lastExport ? (
                  <>
                    <div className={styles.lastFile}>
                      <div className={styles.lastFileIcon}>
                        <FileSpreadsheet size={20} />
                      </div>
                      <div className={styles.lastFileInfo}>
                        <span className={styles.lastFileName}>{lastExport.filename}</span>
                        <span className={styles.lastFileMeta}>
                          <Calendar size={11} />
                          {formatDateTime(lastExport.created_at)}
                        </span>
                      </div>
                    </div>
                    {lastExport.download_url && (
                      <div className={styles.lastButton}>
                        <Button
                          variant="soft"
                          onClick={() =>
                            void api.downloadExport(lastExport.id, lastExport.filename)
                          }
                        >
                          <Download size={14} />
                          Descarregar último
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className={styles.note}>Ainda não foi gerado nenhum ficheiro.</p>
                )}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

function Pagination({ page, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;
  const pages = paginationRange(page, totalPages);
  return (
    <div className={styles.pagination}>
      <button
        type="button"
        className={styles.pageBtn}
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        aria-label="Página anterior"
      >
        ‹
      </button>
      {pages.map((p, idx) =>
        p === "…" ? (
          <span key={`gap-${idx}`} className={styles.pageBtn} aria-hidden>
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ""}`}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        ),
      )}
      <button
        type="button"
        className={styles.pageBtn}
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        aria-label="Página seguinte"
      >
        ›
      </button>
    </div>
  );
}

function paginationRange(current: number, total: number): (number | "…")[] {
  const delta = 1;
  const range: (number | "…")[] = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);

  range.push(1);
  if (left > 2) range.push("…");
  for (let i = left; i <= right; i += 1) range.push(i);
  if (right < total - 1) range.push("…");
  if (total > 1) range.push(total);

  return range;
}
